import { useLayoutEffect, useRef, type TextareaHTMLAttributes } from 'react';

type AutoGrowTextareaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange' | 'value'
> & {
  value: string;
  onValueChange: (value: string) => void;
  autoGrow?: boolean;
  smartIndent?: boolean;
};

export default function AutoGrowTextarea({
  value,
  onValueChange,
  autoGrow = true,
  smartIndent = true,
  ...props
}: AutoGrowTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea || !autoGrow) {
      return;
    }

    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [autoGrow, value]);

  return (
    <textarea
      {...props}
      ref={textareaRef}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      onKeyDown={(event) => {
        props.onKeyDown?.(event);

        if (!smartIndent || event.defaultPrevented || event.key !== 'Enter') {
          return;
        }

        const textarea = event.currentTarget;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const lineStart = value.lastIndexOf('\n', start - 1) + 1;
        const currentLine = value.slice(lineStart, start);
        const currentIndent = currentLine.match(/^\s*/)?.[0] ?? '';
        const extraIndent = /(?:\[|\{)\s*$/.test(currentLine) ? '  ' : '';
        const indentation = currentIndent + extraIndent;
        const nextValue = `${value.slice(0, start)}\n${indentation}${value.slice(end)}`;
        const nextCursor = start + 1 + indentation.length;

        event.preventDefault();
        onValueChange(nextValue);
        window.requestAnimationFrame(() => {
          textarea.selectionStart = nextCursor;
          textarea.selectionEnd = nextCursor;
        });
      }}
    />
  );
}
